import { Flame, Droplets, BatteryCharging, Sun } from 'lucide-react';

/**
 * Puissance installée du parc, par type d'équipement.
 *
 * La PAC est le seul type à porter deux puissances : le matériel est vendu en
 * puissance thermique, mais un agrégateur rémunère l'effacement électrique. Les
 * afficher côte à côte, avec le COP qui les relie, évite qu'un chiffre soit repris
 * pour l'autre dans une négociation.
 *
 * Le serveur sert cette route depuis le portage du tableau de bord, mais aucun écran
 * ne la lisait : la puissance du parc n'était visible nulle part.
 */
const TYPES = [
  { cle: 'PAC', libelle: 'PAC', Icone: Flame, teinte: 'bg-orange-50 text-orange-600' },
  { cle: 'Ballon', libelle: 'Ballon ECS', Icone: Droplets, teinte: 'bg-cyan-50 text-cyan-600' },
  { cle: 'Batterie', libelle: 'Batterie', Icone: BatteryCharging, teinte: 'bg-violet-50 text-violet-600' },
  { cle: 'PV', libelle: 'PV', Icone: Sun, teinte: 'bg-amber-50 text-amber-600' },
];

const kw = (valeur) =>
  `${Number(valeur || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} kW`;

export default function PowerSummaryPanel({ data }) {
  if (!data) return null;
  const parType = data.by_type || {};
  const cop = data.pac_cop;
  const total = TYPES.reduce((somme, { cle }) => somme + (parType[cle]?.puissance_kw || 0), 0);

  return (
    <div className="space-y-3" data-testid="dashboard-power-summary">
      <div className="flex items-baseline justify-between flex-wrap gap-1">
        <div>
          <h2 className="text-[15px] font-medium text-neutral-900">Puissance installée</h2>
          <p className="text-[13px] text-neutral-400">
            Total du parc actif : <span className="font-medium text-neutral-700">{kw(total.toFixed(2))}</span>
          </p>
        </div>
        <p className="text-[12px] text-neutral-400">
          COP PAC utilisé : <span className="font-medium text-neutral-600">{cop}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {TYPES.map(({ cle, libelle, Icone, teinte }) => {
          const noeud = parType[cle] || { count: 0, puissance_kw: 0 };
          return (
            <div
              key={cle}
              className="p-4 rounded-lg border border-neutral-200/80 bg-white"
              data-testid={`power-card-${cle}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${teinte}`}>
                  <Icone className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[16px] font-medium text-neutral-900">{libelle}</p>
                  <p className="text-[12px] text-neutral-400">
                    {noeud.count} actif{noeud.count > 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {cle === 'PAC' ? (
                <div className="space-y-1.5">
                  <div>
                    <p className="text-[12px] uppercase tracking-wider text-neutral-400 font-medium">
                      Puiss. thermique
                    </p>
                    <p className="text-[22px] font-semibold text-neutral-900" data-testid="pac-thermique">
                      {kw(noeud.puissance_thermique_kw)}
                    </p>
                  </div>
                  <div className="pt-1.5 border-t border-neutral-100">
                    <p className="text-[12px] uppercase tracking-wider text-neutral-400 font-medium">
                      Puiss. électrique
                    </p>
                    <p className="text-[18px] font-semibold text-orange-700" data-testid="pac-electrique">
                      {kw(noeud.puissance_electrique_kw)}
                    </p>
                    <p className="text-[12px] text-neutral-400 italic">= thermique ÷ COP {cop}</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-[12px] uppercase tracking-wider text-neutral-400 font-medium">
                    Puissance totale
                  </p>
                  <p className="text-[26px] font-semibold text-neutral-900" data-testid={`power-${cle}-kw`}>
                    {kw(noeud.puissance_kw)}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
